// src/components/PdfHighlighter.jsx
import React, { useCallback, useState, useEffect, useMemo } from "react";
import {
  PdfLoader,
  PdfHighlighter,
  Tip,
  Highlight,
  Popup,
} from "react-pdf-highlighter";
import * as pdfjsLib from "pdfjs-dist";
import api from "../api/api";
import { Trash2, Pencil } from "lucide-react";

// ✅ Use CDN worker URL for PDF.js
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

export default function PdfHighlighterComponent({ materialId, fileUrl, mode = "preview" }) {
  const [highlights, setHighlights] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editTag, setEditTag] = useState("");

  // --- Fetch highlights ---
  useEffect(() => {
    let isMounted = true;
    if (!materialId) return;

    async function loadHighlights() {
      try {
        const res = await api.get(`/prephighlights/${materialId}`);
        if (isMounted) setHighlights(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("❌ Failed to fetch highlights:", err);
      }
    }

    loadHighlights();
    return () => {
      isMounted = false;
      setHighlights([]);
    };
  }, [materialId]);

  // --- CRUD actions ---
  const addHighlight = useCallback(async (highlight) => {
    try {
      const payload = {
        materialId,
        pageNumber: highlight.position.pageNumber,
        boundingRect: highlight.position.boundingRect,
        rects: highlight.position.rects || [],
        colorTag: "yellow",
        content: highlight.content?.text || "",
      };
      const res = await api.post("/prephighlights", payload);
      if (res?.data) setHighlights(prev => [...prev, res.data]);
    } catch (err) {
      console.error("❌ Failed to save highlight", err);
      alert("Failed to save highlight");
    }
  }, [materialId]);

  const updateHighlight = useCallback(async (id, newTag) => {
    try {
      const res = await api.put(`/prephighlights/${materialId}/${id}`, { colorTag: newTag });
      if (res?.data) {
        setHighlights(prev =>
          prev.map(h => (h.id === id ? { ...h, colorTag: res.data.colorTag } : h))
        );
      }
      setEditingId(null);
    } catch (err) {
      console.error("❌ Failed to update highlight", err);
      alert("Failed to update highlight");
    }
  }, [materialId]);

  const deleteHighlight = useCallback(async (id) => {
    if (!window.confirm("Delete this highlight?")) return;
    try {
      await api.delete(`/prephighlights/${materialId}/${id}`);
      setHighlights(prev => prev.filter(h => h.id !== id));
    } catch (err) {
      console.error("❌ Failed to delete highlight", err);
      alert("Failed to delete highlight");
    }
  }, [materialId]);

  // --- Map highlights for PdfHighlighter ---
  const mappedHighlights = useMemo(() =>
    highlights
      .filter(h => h.pageNumber && h.boundingRect)
      .map(h => ({
        id: h.id,
        position: {
          pageNumber: h.pageNumber,
          boundingRect: h.boundingRect,
          rects: h.rects || [],
        },
        comment: { text: h.colorTag },
        content: { text: h.content },
      }))
  , [highlights]);

  // --- Guard for fileUrl ---
  if (!fileUrl) {
    return (
      <div className="flex items-center justify-center h-[80vh] bg-gray-100 rounded-xl">
        <p className="text-gray-500 text-sm">Waiting for PDF...</p>
      </div>
    );
  }

  return (
    <div className="relative w-full h-[80vh] bg-gray-50 rounded-xl">
      {mode === "preview" ? (
        <PdfLoader
          url={fileUrl}
          beforeLoad={
            <div className="flex items-center justify-center h-full text-gray-500">
              Loading PDF…
            </div>
          }
        >
          {pdfDocument =>
            pdfDocument ? (
              <PdfHighlighter
                pdfDocument={pdfDocument}
                enableAreaSelection={event => event.altKey}
                onSelectionFinished={(position, content, hideTip) => (
                  <Tip onConfirm={() => { addHighlight({ position, content }); hideTip(); }} />
                )}
                highlightTransform={(highlight, _, __, ___, ____, isScrolledTo) => (
                  <Popup
                    key={highlight.id}
                    popupContent={
                      <div className="p-2 bg-white shadow rounded">
                        <p className="text-xs mb-1">{highlight.comment?.text || "Highlight"}</p>
                        <button
                          className="flex items-center gap-1 text-xs px-2 py-1 border rounded hover:bg-gray-100"
                          onClick={() => deleteHighlight(highlight.id)}
                        >
                          <Trash2 size={12} /> Delete
                        </button>
                      </div>
                    }
                  >
                    <Highlight {...highlight} isScrolledTo={isScrolledTo} />
                  </Popup>
                )}
                highlights={mappedHighlights}
              />
            ) : (
              <div className="flex items-center justify-center h-full text-red-500">
                Failed to load PDF.
              </div>
            )
          }
        </PdfLoader>
      ) : (
        <div className="p-3 h-full overflow-auto">
          <h3 className="font-semibold mb-3">Saved Highlights</h3>
          {highlights.length === 0 ? (
            <p className="text-gray-500 text-sm">No highlights yet.</p>
          ) : (
            <ul className="space-y-2">
              {highlights.map(h => (
                <li key={h.id} className="p-3 bg-gray-100 border rounded flex justify-between items-center">
                  <div>
                    <p className="text-sm">Page {h.pageNumber} — <span className="font-medium">{h.colorTag}</span></p>
                    {h.content && <p className="text-xs text-gray-600 line-clamp-2">{h.content}</p>}
                  </div>
                  <div className="flex gap-2">
                    {editingId === h.id ? (
                      <>
                        <input
                          className="border px-2 py-1 text-xs rounded"
                          value={editTag}
                          onChange={e => setEditTag(e.target.value)}
                        />
                        <button
                          onClick={() => updateHighlight(h.id, editTag)}
                          className="px-2 py-1 bg-green-600 text-white text-xs rounded"
                        >
                          Save
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => { setEditingId(h.id); setEditTag(h.colorTag); }}
                          className="px-2 py-1 border text-xs rounded flex items-center gap-1"
                        >
                          <Pencil size={12} /> Edit
                        </button>
                        <button
                          onClick={() => deleteHighlight(h.id)}
                          className="px-2 py-1 bg-red-600 text-white text-xs rounded flex items-center gap-1"
                        >
                          <Trash2 size={12} /> Delete
                        </button>
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
